import React, { useEffect, useState } from 'react';
import api from '../../api';
import { useLocation, useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { setProfile } from '../../reducers/UserReducer';
import { setAuthenticated } from '../../reducers/AuthReducer';
import { Link } from 'react-router-dom';
import {
  CAlert,
  CButton,
  CCard,
  CCardBody,
  CCardGroup,
  CCol,
  CContainer,
  CForm,
  CFormInput,
  CFormSwitch,
  CInputGroup,
  CInputGroupText,
  CRow,
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilCursor, cilLockLocked, cilLockUnlocked, cilUser } from '@coreui/icons';
import logo from '../../assets/brand/logo.png';
import { useTranslation } from 'react-i18next';
import { shortedName } from 'src/utils/UtilsFunc';

const FormLogin = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { authenticated } = useSelector((state) => state.auth);
  const { profile } = useSelector((state) => state.user);
  const mail = !!authenticated?.remember ? profile?.email : '';
  const [email, setEmail] = useState(mail);
  const [mfa, setMfa] = useState('');
  const [website, setWebsite] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (authenticated?.isLogged) {
      navigate('/dashboard', { replace: true });
    }
  }, [authenticated?.isLogged, navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage('');
    try {
      await api.auth.csrfToken();
      const aut = await api.auth.login(email, password, mfa, website);
      dispatch(
        setAuthenticated({
          ...authenticated,
          isLogged: true,
          logged_in: JSON.stringify(new Date()),
        }),
      );
      setAccountInfo(aut);
    } catch (error) {
      console.log('ERROR => ', error);
      let message = 'Erro desconhecido. Tente novamente.';
      if (error?.response?.data?.message) {
        message = error.response.data.message;
      } else if (typeof error.message === 'string') {
        message = error.message;
      }
      setErrorMessage(message);
    }
  };

  const setAccountInfo = async (aut) => {
    try {
      dispatch(setProfile(aut));
      navigate('/dashboard');
    } catch (error) {
      console.log('GET ACCOUNT INFO ERROR => ', error);
    }
  };

  const handleRemember = (event) => {
    dispatch(setAuthenticated({ ...authenticated, remember: event.target.checked }));
    dispatch(setProfile({ ...profile, name: '', email: '' }));
  };

  return (
    <div>
      <CCol xs={12}></CCol>
      <div className="bg-body-tertiary min-vh-100 d-flex flex-row align-items-center">
        <CContainer>
          <CRow className="justify-content-center">
            <CCol md={8}>
              {state?.logout && (
                <CAlert color="danger" dismissible>
                  <strong>{t('common.expired.title')}</strong>... {t('common.expired.subtitle')}
                </CAlert>
              )}

              <CCardGroup>
                <CCard className="p-4">
                  <CCardBody>
                    {errorMessage && (
                      <CAlert color="danger" dismissible onClose={() => setErrorMessage('')}>
                        {errorMessage}
                      </CAlert>
                    )}

                    <CForm onSubmit={handleSubmit}>
                      <h1>{t('common.appTitle')}</h1>
                      <p className="text-body-secondary">
                        {!!authenticated?.remember && profile?.name
                          ? `${t('loginScreen.welcome')} ${shortedName(profile?.name)}`
                          : t('loginScreen.title')}
                      </p>

                      <CInputGroup className="mb-3">
                        <CInputGroupText>
                          <CIcon icon={cilUser} />
                        </CInputGroupText>
                        <CFormInput
                          type="email"
                          placeholder={t('loginScreen.email')}
                          autoComplete="email"
                          value={email}
                          onChange={(e) => {
                            setEmail(e.target.value);
                            setErrorMessage('');
                          }}
                          required
                        />
                      </CInputGroup>

                      <CInputGroup className="mb-3">
                        <CInputGroupText>
                          <CIcon icon={cilLockLocked} />
                        </CInputGroupText>
                        <CFormInput
                          type={showPassword ? 'text' : 'password'}
                          placeholder={t('loginScreen.password')}
                          autoComplete="current-password"
                          value={password}
                          onChange={(e) => {
                            setPassword(e.target.value);
                            setErrorMessage('');
                          }}
                          required
                        />
                        <CButton
                          type="button"
                          color="secondary"
                          variant="outline"
                          onClick={() => setShowPassword((v) => !v)}
                          aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                          title={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                        >
                          <CIcon icon={showPassword ? cilLockUnlocked : cilLockLocked} />
                        </CButton>
                      </CInputGroup>

                      <CInputGroup className="mb-3">
                        <CInputGroupText>
                          <CIcon icon={cilCursor} />
                        </CInputGroupText>
                        <CFormInput
                          type="text"
                          placeholder={t('loginScreen.mfa')}
                          value={mfa}
                          onChange={(e) => {
                            setMfa(e.target.value);
                            setErrorMessage('');
                          }}
                          required
                        />
                      </CInputGroup>

                      <CInputGroup className="mb-4">
                        <CFormSwitch
                          label={t('loginScreen.remember')}
                          id="formSwitchCheckDefault"
                          checked={authenticated?.remember}
                          onChange={handleRemember}
                        />
                      </CInputGroup>

                      <CRow>
                        <CCol xs={12}>
                          <div className="d-grid gap-2">
                            <CButton color="primary" className="px-4" type="submit">
                              {t('loginScreen.enter')}
                            </CButton>
                          </div>
                        </CCol>
                        <CCol xs={12} className="text-right pt-3">
                          <CButton color="link" className="px-0">
                            {t('loginScreen.forgot')}
                          </CButton>
                        </CCol>
                      </CRow>
                      <input
                        type="text"
                        name="website"
                        value=""
                        tabIndex="-1"
                        autoComplete="off"
                        style={{
                          position: 'absolute',
                          left: '-9999px',
                          opacity: 0,
                          height: 0,
                          width: 0,
                        }}
                        onChange={(e) => {
                          setWebsite(e.target.value);
                          setErrorMessage('');
                        }}
                      />
                    </CForm>
                  </CCardBody>
                </CCard>

                <CCard className="text-white bg-primary py-5 text-center">
                  <CCardBody className="d-flex flex-column justify-content-evenly">
                    <div>
                      <img
                        src={logo}
                        width={300}
                        className="logo-form-login"
                        alt="Logo Vai de Cripto"
                        title="Logo Vai de Cripto"
                      />
                    </div>
                    <Link
                      to="https://vaidecripto.com.br"
                      target="_blank"
                      alt="Criptou"
                      title="Criptou"
                      className="mt-5"
                    >
                      <CButton color="primary" active tabIndex={-1}>
                        {t('loginScreen.website')}
                      </CButton>
                    </Link>
                  </CCardBody>
                </CCard>
              </CCardGroup>
            </CCol>
          </CRow>
        </CContainer>
      </div>
    </div>
  );
};

export default FormLogin;
